<template>
    <RegularModal ref="paymentModal" modal-card-class="is-large" @closed="onUpdateModalClose">
        <template v-slot:title>
            <h4 class="modal-title">{{ translate("admin_payments.update_modal.title") }}</h4>
        </template>

        <template v-slot:body>
            <template v-if="initDone && selectedCompetitorPayment">
                <div class="columns">
                    <div class="column">
                        <div class="contact-person" v-if="user">
                            <div>
                                <span class="has-text-grey-light"> {{ translate("admin_payments.contact_person") }}: </span>
                                <span class="clarity-mask"
                                    >{{ user.firstName }} {{ user.lastName }}, {{ user.email }}, {{ user.phone }}</span
                                >
                            </div>
                            <div v-if="user.academy && user.academy.id">
                                <span class="has-text-grey-light"> {{ translate("common.academy") }}:</span>
                                {{ user.academy.name }} / {{ user.branch }}
                            </div>
                        </div>

                        <UnassignedPaymentDocuments :event-id="eventId" :unassigned-payment-documents="unassignedPaymentDocuments" />
                    </div>
                </div>

                <div class="tabs is-boxed">
                    <ul>
                        <li :class="{ 'is-active': !showAllCompetitors }">
                            <a @click="switchEditComponent(false)">
                                {{ translate("admin_payment.selected_competitor") }}
                            </a>
                        </li>
                        <li :class="{ 'is-active': showAllCompetitors }">
                            <a @click="switchEditComponent(true)">
                                {{ translate("admin_payment.all_competitors_for_user") }}
                            </a>
                        </li>
                    </ul>
                </div>

                <SelectedCompetitorPaymentEdit
                    :selected-competitor-payment="selectedCompetitorPayment"
                    :event-id="eventId"
                    :event-timezone="eventTimezone"
                    @selectedCompetitorUpdated="onSelectedCompetitorUpdated"
                    v-if="!showAllCompetitors"
                />

                <CompetitorTableEdit
                    :event-id="eventId"
                    :event-timezone="eventTimezone"
                    :competitor-payments="competitorPayments"
                    :user="user"
                    @competitorPaymentsUpdated="onCompetitorPaymentsUpdated"
                    v-if="showAllCompetitors"
                />
            </template>
            <div v-else class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>

        <template v-slot:footer>
            <button @click="closeModal" class="button">
                {{ translate("common.close") }}
            </button>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import { useRegisteredCompetitorsStore } from "../registeredCompetitorsStore";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import paymentsApi, { CompetitorPaymentWithMetadata } from "../../Api/payments";
    import eventUsersApi, { ComplexUser } from "../../Api/eventUsers";
    import RegularModal from "../../Common/RegularModal.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import UnassignedPaymentDocuments from "./UnassignedPaymentDocuments.vue";
    import SelectedCompetitorPaymentEdit from "./SelectedCompetitorPaymentEdit.vue";
    import CompetitorTableEdit from "./CompetitorTableEdit.vue";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const store = useRegisteredCompetitorsStore();
    const paymentModal = ref<InstanceType<typeof RegularModal> | null>(null);

    const initDone = ref(false);
    const selectedCompetitorPayment = ref<CompetitorPaymentWithMetadata | null>(null);
    const competitorPayments = ref<CompetitorPaymentWithMetadata[]>([]);
    const showAllCompetitors = ref(false);
    const user = ref<ComplexUser | null>(null);
    const unassignedPaymentDocuments = ref<any[]>([]);

    function resetData() {
        initDone.value = false;
        unassignedPaymentDocuments.value = [];
        showAllCompetitors.value = false;
        competitorPayments.value = [];
        user.value = null;
    }

    function closeModal() {
        paymentModal.value?.close();
    }

    function onUpdateModalClose() {
        store.getRegisteredCompetitorsPage();
    }

    function fetchCompetitorPaymentsForUser(userId: number) {
        return paymentsApi
            .getCompetitorPaymentsPage(props.eventId, {
                offset: 0,
                limit: 1000,
                userId,
            })
            .then(competitorPaymentsResponse => {
                competitorPayments.value = competitorPaymentsResponse.result;
            });
    }

    function fetchUser(userId: number) {
        return eventUsersApi.getComplexUser(props.eventId, userId).then(userData => {
            user.value = userData;
        });
    }

    function fetchSingleCompetitorPayment(eventCompetitorId: number, userId: number) {
        return paymentsApi.getSingleCompetitorPayment(props.eventId, eventCompetitorId, userId).then(competitorPayment => {
            selectedCompetitorPayment.value = competitorPayment;
            return competitorPayment;
        });
    }

    function fetchUnassignedPaymentDocuments(userId: number) {
        return paymentsApi.getUnassignedPaymentDocumentsListing(props.eventId, userId).then(docs => {
            unassignedPaymentDocuments.value = docs;
            return docs;
        });
    }

    function switchEditComponent(showAll: boolean) {
        if (!selectedCompetitorPayment.value) {
            return;
        }

        showAllCompetitors.value = showAll;

        if (showAllCompetitors.value) {
            return fetchCompetitorPaymentsForUser(selectedCompetitorPayment.value.userId || 0).catch(handleError);
        } else {
            return fetchSingleCompetitorPayment(selectedCompetitorPayment.value.id || 0, selectedCompetitorPayment.value.userId || 0).catch(
                handleError,
            );
        }
    }

    function open(competitor: CompetitorPaymentWithMetadata) {
        resetData();
        selectedCompetitorPayment.value = competitor;
        paymentModal.value?.open();

        return Promise.all([
            fetchUser(competitor.userId || 0),
            fetchCompetitorPaymentsForUser(competitor.userId || 0),
            fetchUnassignedPaymentDocuments(competitor.userId || 0),
        ])
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function onSelectedCompetitorUpdated(updatedCompetitorPayment) {
        selectedCompetitorPayment.value = updatedCompetitorPayment;
    }

    function onCompetitorPaymentsUpdated(updatedCompetitorPayments) {
        competitorPayments.value = updatedCompetitorPayments;
    }

    defineExpose({
        open,
        closeModal,
    });
</script>

<style>
    .table td {
        vertical-align: middle;
    }

    .table th {
        vertical-align: middle;
    }

    .contact-person {
        margin-bottom: 15px;
    }

    .contact-person div {
        padding: 2px;
    }

    @media (min-width: 1250px) {
        .modal-lg {
            width: 1200px;
        }
    }

    .top-options {
        margin-bottom: 5px;
    }

    .selected-competitor-payment {
        font-size: 18px;
    }

    .more-info-buttons button {
        margin-bottom: 3px;
        min-width: 252px;
    }
</style>
